import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Users, Search } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  full_name: "",
  dob: "",
  phone: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  address: "",
  navigation_address: "",
  permit_number: "",
  assigned_instructor_id: "",
  parent_id: "",
  default_meeting_location: "",
};

export default function Students() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [parents, setParents] = useState([]);
  const [locations, setLocations] = useState([]);
  const activeLocations = useMemo(() => locations.filter((l) => l.active), [locations]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const isStaff = user?.role === "admin" || user?.role === "office_staff";

  const load = useCallback(async () => {
    try {
      const promises = [api.get("/students"), api.get("/settings/meeting-locations")];
      if (isStaff) {
        promises.push(api.get("/instructors"));
        promises.push(api.get("/parents"));
      }
      const results = await Promise.all(promises);
      setStudents(results[0].data);
      setLocations(results[1].data);
      if (isStaff) {
        setInstructors(results[2].data);
        setParents(results[3].data);
      }
    } catch {
      toast.error("Could not load students");
    } finally {
      setLoading(false);
    }
  }, [isStaff]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!form.full_name) {
      toast.error("Student name is required");
      return;
    }
    if (isStaff && !form.parent_id) {
      toast.error("Please select a parent");
      return;
    }
    if (!form.default_meeting_location) {
      toast.error("Please select a default meeting location");
      return;
    }
    try {
      const payload = { ...form };
      if (!isStaff) delete payload.parent_id;
      await api.post("/students", payload);
      toast.success("Student added");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add student");
    }
  };

  const filtered = students.filter((s) => s.full_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="students-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search students..." className="pl-9 h-11" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="students-search-input" />
        </div>
        <Button onClick={() => setOpen(true)} className="h-11 bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="add-student-button">
          <Plus className="h-4 w-4 mr-2" /> Add Student
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-64 rounded-[var(--radius)]" />
      ) : filtered.length === 0 ? (
        <div className="rounded-[var(--radius)] border bg-card p-12 text-center space-y-2" data-testid="students-empty-state">
          <Users className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No students found yet.</p>
        </div>
      ) : (
        <div className="rounded-[var(--radius)] border bg-card overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden sm:table-cell">Phone</TableHead>
                <TableHead className="hidden md:table-cell">Instructor</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow
                  key={s.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/students/${s.id}`)}
                  data-testid="student-row"
                >
                  <TableCell className="font-medium">{s.full_name}</TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">{s.phone || "-"}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{s.assigned_instructor_name || "Unassigned"}</TableCell>
                  <TableCell className="capitalize">{s.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="add-student-dialog">
          <DialogHeader>
            <DialogTitle>Add Student</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            {isStaff && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Parent</Label>
                <select
                  className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm"
                  value={form.parent_id}
                  onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
                  data-testid="student-form-parent-select"
                >
                  <option value="">Select parent</option>
                  {parents.map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name} ({p.email})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} data-testid="student-form-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Date of birth</Label>
              <Input type="date" value={form.dob} onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))} data-testid="student-form-dob-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="student-form-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Emergency contact name</Label>
              <Input value={form.emergency_contact_name} onChange={(e) => setForm((f) => ({ ...f, emergency_contact_name: e.target.value }))} data-testid="student-form-emergency-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Emergency contact phone</Label>
              <Input value={form.emergency_contact_phone} onChange={(e) => setForm((f) => ({ ...f, emergency_contact_phone: e.target.value }))} data-testid="student-form-emergency-phone-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Address / Pickup location</Label>
              <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value, navigation_address: e.target.value }))} data-testid="student-form-address-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Permit number (optional)</Label>
              <Input value={form.permit_number} onChange={(e) => setForm((f) => ({ ...f, permit_number: e.target.value }))} data-testid="student-form-permit-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="flex items-center gap-1.5">Default meeting location <span className="text-destructive">*</span></Label>
              <select
                className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm"
                value={form.default_meeting_location}
                onChange={(e) => setForm((f) => ({ ...f, default_meeting_location: e.target.value }))}
                data-testid="student-form-meeting-location-select"
              >
                <option value="">Select where this student is usually picked up</option>
                {activeLocations.map((l) => (
                  <option key={l.id} value={l.name}>{l.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">Shown automatically on every scheduled lesson and reminder.</p>
            </div>
            {isStaff && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Assign instructor (optional)</Label>
                <select
                  className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm"
                  value={form.assigned_instructor_id}
                  onChange={(e) => setForm((f) => ({ ...f, assigned_instructor_id: e.target.value }))}
                  data-testid="student-form-instructor-select"
                >
                  <option value="">Unassigned</option>
                  {instructors.map((i) => (
                    <option key={i.id} value={i.id}>{i.full_name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="student-form-submit-button">Save Student</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
